/**
 * Source-indexed progress rules for the thirteen original courses.
 *
 * The v1 simulation did not infer race progress from a global nearest point
 * or from physical distance.  It kept a local centreline index, admitted at
 * most one forward gate per tick, and ranked karts with monotonically
 * increasing gates-passed plus a fraction of the current gate interval.  The
 * small state machine below keeps that rule independent from authored-course
 * distance progress.  The caller owns projection and supplies the current
 * local sample index.
 */

export interface ClassicProgressSample {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly distance: number;
}

export interface ClassicProgressRules {
  readonly samples: readonly ClassicProgressSample[];
  /** Physical route length retained alongside the source-index rules. */
  readonly length: number;
  /** Source sample indices for the ordered gates around one lap. */
  readonly checkpoints: readonly number[];
  /** Maximum planar distance from the localized centreline sample. */
  readonly gateHalfWidth: number;
}

export interface ClassicProgressState {
  x: number;
  z: number;
  classicSampleIndex?: number;
  classicNextCheckpoint?: number;
  classicGatesPassed?: number;
  classicProgress?: number;
}

const GATE_APPROACH_SAMPLES = 26;
const FRACTIONAL_PROGRESS_LIMIT = 0.999;
const ENDPOINT_EPSILON = 1e-7;

/**
 * A prepared v2 layout includes one exact closing sample.  Tests and small
 * callers may provide a closed lap without that duplicate, so detect the
 * duplicate from its coordinates rather than making the API expose another
 * count field.  The returned count is the modulus used by source indices.
 */
function sourceSampleCount(rules: ClassicProgressRules): number {
  const count = rules.samples.length;
  if (count <= 1) return count;
  const first = rules.samples[0];
  const last = rules.samples[count - 1];
  if (Math.abs(first.x - last.x) <= ENDPOINT_EPSILON
    && Math.abs(first.z - last.z) <= ENDPOINT_EPSILON
    && last.distance > first.distance
    && (!Number.isFinite(rules.length) || last.distance >= rules.length - ENDPOINT_EPSILON)) {
    return count - 1;
  }
  return count;
}

function wrappedIndex(index: number, count: number): number {
  const wrapped = index % count;
  return wrapped < 0 ? wrapped + count : wrapped;
}

/**
 * Return the signed shortest local-index movement.  A positive result is a
 * forward movement on the source route; a negative result is reverse.  The
 * half-lap tie is deliberately not treated as forward because it cannot be
 * disambiguated from a reverse jump without another observation.
 */
function shortestIndexDelta(current: number, previous: number, count: number): number {
  let delta = current - previous;
  const half = count / 2;
  if (Math.abs(delta) === half) return 0;
  if (delta > half) delta -= count;
  else if (delta < -half) delta += count;
  return delta;
}

/**
 * Advance one localized classic-course observation.
 *
 * `previousSampleIndex` is the index used for the preceding observation;
 * `state.classicSampleIndex` is the newly projected local index.  The
 * function mutates only the optional classic fields on `state`, performs no
 * collection allocation, and returns true only when checkpoint zero is passed
 * after the initial start-line arm.
 */
export function advanceClassicCheckpoint(
  state: ClassicProgressState,
  rules: ClassicProgressRules,
  previousSampleIndex: number,
): boolean {
  const sampleCount = sourceSampleCount(rules);
  const checkpointCount = rules.checkpoints.length;
  if (sampleCount <= 0 || checkpointCount <= 0) {
    state.classicNextCheckpoint = 0;
    state.classicGatesPassed = 0;
    state.classicProgress = 0;
    return false;
  }

  const currentIndex = state.classicSampleIndex;
  if (!Number.isFinite(currentIndex) || !Number.isFinite(previousSampleIndex)) {
    // A projection is required before source progress can move.  Keep the
    // optional fields in a deterministic initial state for the next tick.
    if (!Number.isFinite(state.classicNextCheckpoint)) state.classicNextCheckpoint = 0;
    if (!Number.isFinite(state.classicGatesPassed)) state.classicGatesPassed = 0;
    if (!Number.isFinite(state.classicProgress)) state.classicProgress = 0;
    return false;
  }

  const current = wrappedIndex(Math.trunc(currentIndex!), sampleCount);
  const previous = wrappedIndex(Math.trunc(previousSampleIndex), sampleCount);
  const nextCheckpoint = Number.isFinite(state.classicNextCheckpoint)
    ? wrappedIndex(Math.trunc(state.classicNextCheckpoint!), checkpointCount)
    : 0;
  let gatesPassed = Number.isFinite(state.classicGatesPassed)
    ? Math.max(0, Math.floor(state.classicGatesPassed!))
    : 0;
  let passedLapStart = false;

  state.classicSampleIndex = current;
  state.classicNextCheckpoint = nextCheckpoint;
  state.classicGatesPassed = gatesPassed;

  // The source gate test is intentionally local: only a positive shortest
  // movement may receive credit, and the current sample must still be within
  // the source's 26-sample post-gate window.  This admits no skipped gates,
  // reverse crossings, or repeated stationary observations.
  const movedForward = shortestIndexDelta(current, previous, sampleCount) > 0;
  const targetIndex = wrappedIndex(Math.trunc(rules.checkpoints[nextCheckpoint]), sampleCount);
  const past = wrappedIndex(current - targetIndex, sampleCount);
  const currentSample = rules.samples[current];
  const nearCentreline = currentSample !== undefined
    && Math.hypot(state.x - currentSample.x, state.z - currentSample.z) <= rules.gateHalfWidth;
  if (movedForward && past < GATE_APPROACH_SAMPLES && nearCentreline) {
    // Gate zero arms the source counter on the first pass.  Only later gate
    // zero crossings represent completed laps.
    passedLapStart = nextCheckpoint === 0 && gatesPassed > 0;
    gatesPassed += 1;
    state.classicGatesPassed = gatesPassed;
    state.classicNextCheckpoint = (nextCheckpoint + 1) % checkpointCount;
  }

  // v1's `prog` is measured in source sample-index space.  Keep the exact
  // 0.999 cap so a kart cannot outrank the next gate before it has crossed it.
  const activeNextCheckpoint = state.classicNextCheckpoint!;
  const previousCheckpoint = (activeNextCheckpoint + checkpointCount - 1) % checkpointCount;
  const previousGate = wrappedIndex(Math.trunc(rules.checkpoints[previousCheckpoint]), sampleCount);
  const nextGate = wrappedIndex(Math.trunc(rules.checkpoints[activeNextCheckpoint]), sampleCount);
  const segment = wrappedIndex(nextGate - previousGate, sampleCount) || 1;
  const withinSegment = wrappedIndex(current - previousGate, sampleCount);
  const fraction = Math.min(FRACTIONAL_PROGRESS_LIMIT, withinSegment / segment);
  state.classicProgress = gatesPassed + fraction * FRACTIONAL_PROGRESS_LIMIT;
  return passedLapStart;
}

/** Return completed source laps, excluding the initial start-line arm. */
export function classicCompletedLaps(state: ClassicProgressState, rules: ClassicProgressRules): number {
  const gatesPassed = Number.isFinite(state.classicGatesPassed) ? state.classicGatesPassed! : 0;
  const checkpointCount = rules.checkpoints.length;
  if (checkpointCount <= 0) return 0;
  return Math.floor(Math.max(0, gatesPassed - 1) / checkpointCount);
}
